import React, { useRef, useEffect } from 'react';
import { ArrowLeft, Send, Paperclip, Reply, SmilePlus, X, Sparkles, Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ReadReceipt } from './ReadReceipt';
import { FileMessage } from './FileMessage';

const TOVI_BOT_ID = 'tovi-bot-00000000-0000-0000-0000-000000000000';

const REACTION_EMOJIS = ['👍', '❤️', '😂', '🎉', '👏', '🔥'];

function formatDateLabel(dateStr) {
  const d = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: d.getFullYear() !== today.getFullYear() ? 'numeric' : undefined });
}

function DateSeparator({ dateStr }) {
  return (
    <div className="flex items-center justify-center my-2" data-testid={`date-separator-${dateStr}`}>
      <div className="flex-1 border-t border-slate-200" />
      <span className="mx-3 text-[10px] font-bold text-slate-400 bg-slate-50 px-2 py-0.5 rounded-full uppercase tracking-wider">
        {formatDateLabel(dateStr)}
      </span>
      <div className="flex-1 border-t border-slate-200" />
    </div>
  );
}

export function ChatView({
  activeConv, messages, memberCount, msgText, typingUsers,
  sending, uploading, replyTo, showReactionPicker, userId,
  isTovi, toviTyping,
  getConvDisplayName, onBack, onSendMessage, onInputChange,
  onFileUpload, onSetReplyTo, onToggleReaction,
  onSetShowReactionPicker,
}) {
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom when messages change or tovi is typing
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, toviTyping]);

  return (
    <>
      <div className={`px-3 py-2.5 border-b border-slate-100 flex items-center gap-2 ${isTovi ? 'bg-gradient-to-r from-amber-500 to-orange-500' : 'bg-gradient-to-r from-blue-600 to-indigo-600'}`}>
        <Button data-testid="chat-back-btn" variant="ghost" size="sm" onClick={onBack} className="text-white hover:bg-white/20 h-7 w-7 p-0">
          <ArrowLeft size={16} />
        </Button>
        {isTovi && (
          <div className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center">
            <Sparkles size={14} className="text-white" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-bold text-white truncate">{getConvDisplayName(activeConv)}</p>
            {isTovi && <span className="text-[9px] font-semibold bg-white/20 text-white px-1.5 py-0.5 rounded-full">AI Assistant</span>}
          </div>
          {isTovi ? (
            <p className="text-[10px] text-amber-100">{toviTyping ? 'Thinking...' : 'Always upbeat, always ready!'}</p>
          ) : typingUsers.length > 0 ? (
            <p className="text-[10px] text-green-300 italic animate-pulse" data-testid="typing-indicator">
              {typingUsers.length === 1 ? `${typingUsers[0]} is typing...` : `${typingUsers.join(', ')} are typing...`}
            </p>
          ) : (
            <p className="text-[10px] text-blue-200">{activeConv.type === 'dm' ? 'Direct Message' : activeConv.type === 'broadcast' ? 'Broadcast' : `${activeConv.members?.length || 0} members`}</p>
          )}
        </div>
      </div>
      <div className={`flex-1 overflow-y-auto px-3 py-2 space-y-1.5 ${isTovi ? 'bg-amber-50/30' : 'bg-slate-50'}`} onClick={() => onSetShowReactionPicker(null)}>
        {messages.length === 0 ? (
          <p className="text-center text-xs text-slate-400 py-8">{isTovi ? 'Say hello to Tovi!' : 'No messages yet. Say hello!'}</p>
        ) : messages.map((m, idx) => {
          const isMe = m.sender_id === userId;
          const isBotMsg = m.sender_id === TOVI_BOT_ID;
          const hasFile = m.file_url;
          const reactions = m.reactions || {};
          const hasReactions = Object.keys(reactions).length > 0;
          const msgDate = new Date(m.created_at).toDateString();
          const prevDate = idx > 0 ? new Date(messages[idx - 1].created_at).toDateString() : null;
          const showDateSep = idx === 0 || msgDate !== prevDate;
          return (
            <React.Fragment key={m.id}>
              {showDateSep && <DateSeparator dateStr={m.created_at} />}
              <div data-testid={`msg-${m.id}`} className={`flex ${isMe ? 'justify-end' : 'justify-start'} group relative`}>
              <div className="max-w-[75%]">
                {/* Bot avatar for Tovi messages */}
                {isBotMsg && isTovi && (
                  <div className="flex items-center gap-1 mb-0.5">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center ${m.is_notification ? 'bg-gradient-to-br from-blue-400 to-indigo-500' : 'bg-gradient-to-br from-amber-400 to-orange-500'}`}>
                      {m.is_notification ? <Bell size={10} className="text-white" /> : <Sparkles size={10} className="text-white" />}
                    </div>
                    <span className={`text-[10px] font-bold ${m.is_notification ? 'text-blue-700' : 'text-amber-700'}`}>
                      {m.is_notification ? 'Notification' : 'Tovi'}
                    </span>
                  </div>
                )}
                {m.reply_to && (
                  <div className={`px-2 py-1 mb-0.5 rounded-t-lg text-[10px] border-l-2 ${isMe ? 'bg-blue-500/30 border-blue-300 text-blue-100' : 'bg-slate-100 border-blue-400 text-slate-500'}`} data-testid={`reply-ref-${m.id}`}>
                    <span className="font-bold">{m.reply_to.sender_name}</span>: {m.reply_to.text}
                  </div>
                )}
                <div className={`px-3 py-2 rounded-2xl text-sm ${
                  isBotMsg && isTovi && m.is_notification
                    ? 'bg-blue-50 border border-blue-200 text-slate-800 rounded-bl-md shadow-sm'
                    : isBotMsg && isTovi
                      ? 'bg-white border border-amber-200 text-slate-800 rounded-bl-md shadow-sm'
                      : isMe
                        ? 'bg-blue-600 text-white rounded-br-md'
                        : 'bg-white border border-slate-200 text-slate-800 rounded-bl-md'
                } ${m.reply_to ? 'rounded-t-none' : ''}`}>
                  {!isMe && !isBotMsg && <p className="text-[10px] font-bold text-blue-600 mb-0.5">{m.sender_name}</p>}
                  {hasFile && <FileMessage message={m} isMe={isMe} />}
                  {m.text && <p className="break-words leading-relaxed whitespace-pre-wrap">{m.text}</p>}
                  <div className="flex items-center justify-end gap-0.5 mt-0.5">
                    <span className={`text-[9px] ${isMe ? 'text-blue-200' : 'text-slate-400'}`}>
                      {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                    {!isBotMsg && <ReadReceipt message={m} userId={userId} memberCount={memberCount} />}
                  </div>
                </div>
                {hasReactions && (
                  <div className="flex flex-wrap gap-0.5 mt-0.5" data-testid={`reactions-${m.id}`}>
                    {Object.entries(reactions).map(([emoji, users]) => (
                      <button key={emoji} onClick={(e) => { e.stopPropagation(); onToggleReaction(m.id, emoji); }}
                        className={`px-1.5 py-0.5 rounded-full text-[11px] border transition-colors ${users.includes(userId) ? 'bg-blue-100 border-blue-300' : 'bg-slate-50 border-slate-200 hover:bg-slate-100'}`}>
                        {emoji} <span className="text-[9px] font-bold text-slate-600">{users.length}</span>
                      </button>
                    ))}
                  </div>
                )}
                <div className={`${isTovi ? 'hidden' : 'hidden group-hover:flex'} absolute ${isMe ? 'left-0 -translate-x-full' : 'right-0 translate-x-full'} top-0 items-center gap-0.5 px-1`}>
                  <button data-testid={`react-btn-${m.id}`} onClick={(e) => { e.stopPropagation(); onSetShowReactionPicker(showReactionPicker === m.id ? null : m.id); }}
                    className="w-6 h-6 rounded-full bg-white border border-slate-200 flex items-center justify-center hover:bg-blue-50 transition-colors shadow-sm">
                    <SmilePlus size={12} className="text-slate-500" />
                  </button>
                  <button data-testid={`reply-btn-${m.id}`} onClick={() => onSetReplyTo({ id: m.id, text: m.text || '[File]', sender_name: m.sender_name })}
                    className="w-6 h-6 rounded-full bg-white border border-slate-200 flex items-center justify-center hover:bg-blue-50 transition-colors shadow-sm">
                    <Reply size={12} className="text-slate-500" />
                  </button>
                </div>
                {showReactionPicker === m.id && (
                  <div className="flex gap-0.5 bg-white border border-slate-200 rounded-full px-1.5 py-1 shadow-lg mt-0.5 w-fit" data-testid={`reaction-picker-${m.id}`} onClick={(e) => e.stopPropagation()}>
                    {REACTION_EMOJIS.map(e => (
                      <button key={e} onClick={() => onToggleReaction(m.id, e)} className="w-7 h-7 rounded-full hover:bg-blue-50 flex items-center justify-center text-base transition-transform hover:scale-125">{e}</button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            </React.Fragment>
          );
        })}
        {/* Tovi thinking indicator */}
        {isTovi && toviTyping && (
          <div className="flex justify-start" data-testid="tovi-thinking">
            <div className="flex items-start gap-1.5">
              <div className="w-5 h-5 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center mt-1">
                <Sparkles size={10} className="text-white animate-pulse" />
              </div>
              <div className="bg-white border border-amber-200 rounded-2xl rounded-bl-md px-3 py-2 shadow-sm">
                <div className="flex gap-1 items-center">
                  <span className="w-1.5 h-1.5 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-1.5 h-1.5 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-1.5 h-1.5 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Regular typing indicator */}
        {!isTovi && typingUsers.length > 0 && (
          <div className="flex justify-start" data-testid="typing-bubble">
            <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-md px-3 py-2">
              <div className="flex gap-1 items-center">
                <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      <div className="px-3 py-2 border-t border-slate-100 bg-white">
        {!isTovi && replyTo && (
          <div className="flex items-center gap-2 mb-1.5 px-2 py-1.5 bg-blue-50 border-l-2 border-blue-500 rounded-r text-xs" data-testid="reply-bar">
            <Reply size={12} className="text-blue-500 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="font-bold text-blue-800">{replyTo.sender_name}</span>
              <p className="text-slate-500 truncate">{replyTo.text}</p>
            </div>
            <button onClick={() => onSetReplyTo(null)} className="text-slate-400 hover:text-slate-600"><X size={14} /></button>
          </div>
        )}
        <div className="flex gap-2 items-center">
          {!isTovi && (
            <>
              <input ref={fileInputRef} type="file" className="hidden" data-testid="chat-file-input"
                accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.xlsx,.csv,.txt,.pptx" onChange={onFileUpload} />
              <Button data-testid="chat-attach-btn" variant="ghost" size="sm" onClick={() => fileInputRef.current?.click()}
                disabled={uploading} className="text-slate-400 hover:text-blue-600 h-9 w-9 p-0 rounded-full">
                {uploading ? <span className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" /> : <Paperclip size={18} />}
              </Button>
            </>
          )}
          <Input data-testid="chat-message-input" value={msgText} onChange={onInputChange}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSendMessage(); } }}
            placeholder={isTovi ? "Ask Tovi anything..." : "Type a message..."} className={`flex-1 h-9 text-sm rounded-full ${isTovi ? 'border-amber-200 focus-visible:ring-amber-400' : 'border-slate-200'}`}
            disabled={isTovi && toviTyping} />
          <Button data-testid="chat-send-btn" onClick={onSendMessage} disabled={!msgText.trim() || sending || (isTovi && toviTyping)}
            className={`${isTovi ? 'bg-amber-500 hover:bg-amber-600' : 'bg-blue-600 hover:bg-blue-700'} text-white rounded-full h-9 w-9 p-0`}>
            <Send size={16} />
          </Button>
        </div>
      </div>
    </>
  );
}
